import {
  AssociatedContact,
  IAssociatedContact,
} from '@app/models/AssociatedContact'
import {
  AssociatedProperty,
  IAssociatedProperty,
} from '@app/models/AssociatedProperty'
import { Comp, IComp } from '@app/models/Comp'
import { Company, ICompany } from '@app/models/Company'
import {
  CompanyContactsResult,
  ICompanyContactsResult,
} from '@app/models/CompanyContactsResult'
import { CompanyResult, ICompanyResult } from '@app/models/CompanyResult'
import { Configuration, IConfiguration } from '@app/models/Configuration'
import {
  ContactEmailAddressesResult,
  IContactEmailAddressesResult,
} from '@app/models/ContactEmailAddressesResult'
import {
  DevelopmentSiteMetadataResponse,
  IDevelopmentSiteMetadataResponse,
} from '@app/models/DevelopmentSiteMetadataResponse'
import { Group, IGroup } from '@app/models/Group'
import { INationalPlace, NationalPlace } from '@app/models/NationalPlace'
import { IPropertyResult, PropertyResult } from '@app/models/PropertyResult'
import { IRole, Role } from '@app/models/Role'
import { IShareContact, ShareContact } from '@app/models/ShareContact'
import { ISignType, SignType } from '@app/models/SignType'
import { ISiteSettings, SiteSettings } from '@app/models/SiteSettings'
import { ISocialNetwork, SocialNetwork } from '@app/models/SocialNetwork'
import { ITeam, Team } from '@app/models/Team'
import { User } from '@app/models/User'
import { ApplicationState } from '@app/store'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import moment from 'moment'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface IGlobalCompaniesState {
  readonly companies: ICompanyResult
}

export interface IGlobalCompanyContactsState {
  readonly companyContacts: ICompanyContactsResult
}

export interface IGlobalCompsState {
  readonly comps: List<IComp>
}

export interface IGlobalConfigurationState {
  readonly configuration: IConfiguration
}

export interface IGlobalContactEmailAddressesState {
  readonly contactEmailAddresses: IContactEmailAddressesResult
}

export interface IGlobalDevelopmentProjectClients {
  readonly developmentSiteMetadata: IDevelopmentSiteMetadataResponse
}

export interface IGlobalGroupsState {
  readonly groups: Group[]
}

export interface IGlobalHbUserState {
  readonly hbContacts: List<IAssociatedContact>
}

export interface IGlobalPlacesState {
  readonly places: List<INationalPlace>
}

export interface IGlobalPropertiesState {
  readonly properties: IPropertyResult
}

export interface IGlobalRolesState {
  readonly roles: List<IRole>
}

export interface IGlobalSignTypesState {
  readonly signTypes: List<ISignType>
}

export interface IGlobalSiteSettingsState {
  readonly siteSettings: ISiteSettings
}

export interface IGlobalSocialNetworksState {
  readonly socialNetworks: List<ISocialNetwork>
}

export interface IGlobalTeamsState {
  readonly teams: List<ITeam>
}

export interface IGlobalState {
  readonly companies: ICompanyResult
  readonly companyContacts: ICompanyContactsResult
  readonly comps: List<IComp>
  readonly configuration: IConfiguration
  readonly contactEmailAddresses: IContactEmailAddressesResult
  readonly developmentSiteMetadata: IDevelopmentSiteMetadataResponse
  readonly groups: List<IGroup>
  readonly hbContacts: List<IAssociatedContact>
  readonly places: List<INationalPlace>
  readonly properties: IPropertyResult
  readonly roles: List<IRole>
  readonly signTypes: List<ISignType>
  readonly siteSettings: ISiteSettings
  readonly socialNetworks: List<ISocialNetwork>
  readonly teams: List<ITeam>
}

const GlobalStateRecord = Record({
  companies: new CompanyResult(),
  companyContacts: new CompanyContactsResult(),
  comps: List<Comp>(),
  configuration: new Configuration(),
  contactEmailAddresses: new ContactEmailAddressesResult(),
  developmentSiteMetadata: new DevelopmentSiteMetadataResponse(),
  groups: List<Group>(),
  hbContacts: List<AssociatedContact>(),
  places: List<NationalPlace>(),
  properties: new PropertyResult(),
  roles: List<Role>(),
  signTypes: List<SignType>(),
  siteSettings: new SiteSettings(),
  socialNetworks: List<SocialNetwork>(),
  teams: List<Team>(),
})

export class GlobalState extends GlobalStateRecord implements IGlobalState {
  public static fromJS(initialState?: Partial<IGlobalState>) {
    return new GlobalState(initialState)
  }

  public static shouldRequestNewData(results: any, timestamp: number) {
    const diff = moment.unix(timestamp).diff(moment(), 'minutes')

    const count = results.size || results.length || 0

    return count === 0 || diff > 1
  }

  public readonly companies: ICompanyResult
  public readonly companyContacts: ICompanyContactsResult
  public readonly comps: List<Comp>
  public readonly configuration: Configuration
  public readonly contactEmailAddresses: ContactEmailAddressesResult
  public readonly developmentSiteMetadata: DevelopmentSiteMetadataResponse
  public readonly groups: List<Group>
  public readonly hbContacts: List<AssociatedContact>
  public readonly places: List<NationalPlace>
  public readonly properties: IPropertyResult
  public readonly roles: List<Role>
  public readonly signTypes: List<SignType>
  public readonly siteSettings: SiteSettings
  public readonly socialNetworks: List<SocialNetwork>
  public readonly teams: List<Team>

  constructor(props?: Partial<IGlobalState>) {
    props ? super(props) : super()
  }
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.
export enum ActionTypes {
  GetCompaniesList = 'GLOBAL::GET_COMPANIES_LIST',
  GetCompanyContactsList = 'GLOBAL::GET_COMPANY_CONTACTS_LIST',
  GetComps = 'GLOBAL::GET_COMPS',
  GetConfiguration = 'GLOBAL::GET_CONFIGURATION',
  GetContactEmailAddresses = 'GLOBAL::GET_CONTACT_EMAIL_ADDRESSES',
  GetDevelopmentSiteMetadata = 'GLOBAL::GET_DEVELOPMENT_SITE_METADATA',
  GetGroupList = 'GLOBAL::GET_GROUPS',
  GetHbContacts = 'GLOBAL::GET_HB_CONTACTS',
  GetPlaces = 'GLOBAL::GET_PLACES',
  GetPropertiesList = 'GLOBAL::GET_PROPERTIES_LIST',
  GetRoles = 'GLOBAL::GET_ROLES',
  GetSignTypes = 'GLOBAL::GET_SIGN_TYPES',
  GetSiteSettings = 'GLOBAL::GET_SITE_SETTINGS',
  GetSocialNetworks = 'GLOBAL::GET_SOCIAL_NETWORKS',
  GetTeams = 'GLOBAL::GET_TEAMS',
  InitializeGlobal = 'GLOBAL::INITIALIZE',
  ResetState = 'GLOBAL::RESET_STATE',
  SetCompaniesList = 'GLOBAL::SET_COMPANIES_LIST',
  SetCompanyContactsList = 'GLOBAL::SET_COMPANY_CONTACTS_LIST',
  SetComps = 'GLOBAL::SET_COMPS',
  SetConfiguration = 'GLOBAL::SET_CONFIGURATION',
  SetContactEmailAddresses = 'GLOBAL::SET_CONTACT_EMAIL_ADDRESSES',
  SetDevelopmentSiteMetadata = 'GLOBAL::SET_DEVELOPMENT_SITE_METADATA',
  SetGroupList = 'GLOBAL::SET_GROUPS',
  SetHbContacts = 'GLOBAL::SET_HB_CONTACTS',
  SetPlaces = 'GLOBAL::SET_PLACES',
  SetPropertiesList = 'GLOBAL::SET_PROPERTIES_LIST',
  SetRoles = 'GLOBAL::SET_ROLES',
  SetSignTypes = 'GLOBAL::SET_SIGN_TYPES',
  SetSiteSettings = 'GLOBAL::SET_SITE_SETTINGS',
  SetSocialNetworks = 'GLOBAL::SET_SOCIAL_NETWORKS',
  SetTeams = 'GLOBAL::SET_TEAMS',
}

interface IGetCompaniesListAction {
  type: ActionTypes.GetCompaniesList
}

interface IGetCompanyContactsListAction {
  type: ActionTypes.GetCompanyContactsList
  forceUpdate?: boolean
}

interface IGetCompsAction {
  filter?: string
  type: ActionTypes.GetComps
}

interface IGetConfigurationAction {
  type: ActionTypes.GetConfiguration
}

interface IGetContactEmailAddressesAction {
  type: ActionTypes.GetContactEmailAddresses
}

interface IGetDevelopmentSiteMetadataAction {
  type: ActionTypes.GetDevelopmentSiteMetadata
}

interface IGetGroupsAction {
  type: ActionTypes.GetGroupList
}

interface IGetHbContactsAction {
  type: ActionTypes.GetHbContacts
}

interface IGetPlacesAction {
  filter?: string
  type: ActionTypes.GetPlaces
}

interface IGetPropertiesListAction {
  type: ActionTypes.GetPropertiesList
}

interface IGetRolesAction {
  type: ActionTypes.GetRoles
}

interface IGetSignTypesAction {
  type: ActionTypes.GetSignTypes
}

interface IGetSiteSettingsAction {
  type: ActionTypes.GetSiteSettings
}

interface IGetSocialNetworksAction {
  type: ActionTypes.GetSocialNetworks
}

interface IGetTeamsAction {
  type: ActionTypes.GetTeams
}

interface IInitializeGlobalAction {
  type: ActionTypes.InitializeGlobal
  user: User
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISetCompaniesListAction {
  companiesResult: ICompanyResult
  type: ActionTypes.SetCompaniesList
}

interface ISetCompanyContactsListAction {
  companyContactsResult: CompanyContactsResult
  type: ActionTypes.SetCompanyContactsList
}

interface ISetCompsAction {
  comps: Comp[]
  type: ActionTypes.SetComps
}

interface ISetConfigurationAction {
  configuration: Configuration
  type: ActionTypes.SetConfiguration
}

interface ISetContactEmailAddressesAction {
  contactEmailAddresses: ContactEmailAddressesResult
  type: ActionTypes.SetContactEmailAddresses
}

interface ISetDevelopmentSiteMetadataAction {
  metadata: IDevelopmentSiteMetadataResponse
  type: ActionTypes.SetDevelopmentSiteMetadata
}

interface ISetGroupsAction {
  groups: Group[]
  type: ActionTypes.SetGroupList
}

interface ISetHbContactsAction {
  contacts: List<AssociatedContact>
  type: ActionTypes.SetHbContacts
}

interface ISetPlacesAction {
  places: NationalPlace[]
  type: ActionTypes.SetPlaces
}

interface ISetPropertiesListAction {
  type: ActionTypes.SetPropertiesList
  propertiesResult: PropertyResult
}

interface ISetRolesAction {
  type: ActionTypes.SetRoles
  roles: List<Role>
}

interface ISetSignTypesAction {
  type: ActionTypes.SetSignTypes
  signTypes: List<SignType>
}

interface ISetSiteSettingsAction {
  siteSettings: SiteSettings
  type: ActionTypes.SetSiteSettings
}

interface ISetSocialNetworksAction {
  type: ActionTypes.SetSocialNetworks
  networks: List<SocialNetwork>
}

interface ISetTeamsAction {
  type: ActionTypes.SetTeams
  teams: List<Team>
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IResetStateAction
  | ISetCompaniesListAction
  | ISetCompanyContactsListAction
  | ISetCompsAction
  | ISetConfigurationAction
  | ISetContactEmailAddressesAction
  | ISetDevelopmentSiteMetadataAction
  | ISetGroupsAction
  | ISetHbContactsAction
  | ISetPlacesAction
  | ISetPropertiesListAction
  | ISetRolesAction
  | ISetSignTypesAction
  | ISetSiteSettingsAction
  | ISetSocialNetworksAction
  | ISetTeamsAction

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
function getCompaniesList(): IGetCompaniesListAction {
  return { type: ActionTypes.GetCompaniesList }
}

export function getCompanyContactsList(forceUpdate?: boolean): IGetCompanyContactsListAction {
  return { type: ActionTypes.GetCompanyContactsList, forceUpdate }
}

export function getComps(filter?: string): IGetCompsAction {
  return { filter, type: ActionTypes.GetComps }
}

function getConfiguration(): IGetConfigurationAction {
  return { type: ActionTypes.GetConfiguration }
}

function getContactEmailAddresses(): IGetContactEmailAddressesAction {
  return { type: ActionTypes.GetContactEmailAddresses }
}

function getDevelopmentSiteMetadata(): IGetDevelopmentSiteMetadataAction {
  return { type: ActionTypes.GetDevelopmentSiteMetadata }
}

function getGroups(): IGetGroupsAction {
  return { type: ActionTypes.GetGroupList }
}

function getHbcontacts(): IGetHbContactsAction {
  return { type: ActionTypes.GetHbContacts }
}

export function getPlaces(filter?: string): IGetPlacesAction {
  return { filter, type: ActionTypes.GetPlaces }
}

function getPropertiesList(): IGetPropertiesListAction {
  return { type: ActionTypes.GetPropertiesList }
}

function getRolesList(): IGetRolesAction {
  return { type: ActionTypes.GetRoles }
}

function getSignTypes(): IGetSignTypesAction {
  return { type: ActionTypes.GetSignTypes }
}

function getSiteSettings(): IGetSiteSettingsAction {
  return { type: ActionTypes.GetSiteSettings }
}

function getSocialNetworksList(): IGetSocialNetworksAction {
  return { type: ActionTypes.GetSocialNetworks }
}

function getTeams(): IGetTeamsAction {
  return { type: ActionTypes.GetTeams }
}

function initializeGlobal(user: User): IInitializeGlobalAction {
  return { type: ActionTypes.InitializeGlobal, user }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function setCompaniesList(
  companiesResult: ICompanyResult,
): ISetCompaniesListAction {
  return { companiesResult, type: ActionTypes.SetCompaniesList }
}

function setCompanyContactsList(
  companyContactsResult: CompanyContactsResult,
): ISetCompanyContactsListAction {
  return { companyContactsResult, type: ActionTypes.SetCompanyContactsList }
}

function setComps(comps: Comp[]): ISetCompsAction {
  return { comps, type: ActionTypes.SetComps }
}

function setConfiguration(
  configuration: Configuration,
): ISetConfigurationAction {
  return { configuration, type: ActionTypes.SetConfiguration }
}

function setContactEmailAddresses(
  contactEmailAddresses: ContactEmailAddressesResult,
): ISetContactEmailAddressesAction {
  return { contactEmailAddresses, type: ActionTypes.SetContactEmailAddresses }
}

function setDevelopmentSiteMetadata(
  metadata: IDevelopmentSiteMetadataResponse,
): ISetDevelopmentSiteMetadataAction {
  return { metadata, type: ActionTypes.SetDevelopmentSiteMetadata }
}

function setGroups(groups: Group[]): ISetGroupsAction {
  return { groups, type: ActionTypes.SetGroupList }
}

function setHbContacts(
  contacts: List<AssociatedContact>,
): ISetHbContactsAction {
  return { contacts, type: ActionTypes.SetHbContacts }
}

function setPlaces(places: NationalPlace[]): ISetPlacesAction {
  return { places, type: ActionTypes.SetPlaces }
}

function setPropertiesList(
  propertiesResult: PropertyResult,
): ISetPropertiesListAction {
  return { propertiesResult, type: ActionTypes.SetPropertiesList }
}

function setRolesList(roles: List<Role>): ISetRolesAction {
  return { roles, type: ActionTypes.SetRoles }
}

function setSignTypes(signTypes: List<SignType>): ISetSignTypesAction {
  return { signTypes, type: ActionTypes.SetSignTypes }
}

function setSiteSettings(siteSettings: SiteSettings): ISetSiteSettingsAction {
  return { siteSettings, type: ActionTypes.SetSiteSettings }
}

function setSocialNetworksList(
  networks: List<SocialNetwork>,
): ISetSocialNetworksAction {
  return { networks, type: ActionTypes.SetSocialNetworks }
}

function setTeams(teams: List<Team>): ISetTeamsAction {
  return { teams, type: ActionTypes.SetTeams }
}

export const actionCreators = {
  getCompaniesList,
  getCompanyContactsList,
  getComps,
  getConfiguration,
  getContactEmailAddresses,
  getDevelopmentSiteMetadata,
  getGroups,
  getHbcontacts,
  getPlaces,
  getPropertiesList,
  getRolesList,
  getSignTypes,
  getSiteSettings,
  getSocialNetworksList,
  getTeams,
  initializeGlobal,
}

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const reducer: Reducer<GlobalState> = (
  state: GlobalState = new GlobalState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.ResetState:
      return new GlobalState()

    case ActionTypes.SetCompaniesList:
      return state.set('companies', action.companiesResult) as GlobalState

    case ActionTypes.SetCompanyContactsList:
      return state.set(
        'companyContacts',
        action.companyContactsResult,
      ) as GlobalState

    case ActionTypes.SetComps:
      return state.set('comps', action.comps) as GlobalState

    case ActionTypes.SetConfiguration:
      return state.set('configuration', action.configuration) as GlobalState

    case ActionTypes.SetContactEmailAddresses:
      return state.set(
        'contactEmailAddresses',
        action.contactEmailAddresses,
      ) as GlobalState

    case ActionTypes.SetDevelopmentSiteMetadata:
      return state.set(
        'developmentSiteMetadata',
        action.metadata,
      ) as GlobalState

    case ActionTypes.SetGroupList:
      return state.set('groups', List(action.groups)) as GlobalState

    case ActionTypes.SetHbContacts:
      return state.set('hbContacts', action.contacts) as GlobalState

    case ActionTypes.SetPlaces:
      return state.set('places', action.places) as GlobalState

    case ActionTypes.SetPropertiesList:
      return state.set('properties', action.propertiesResult) as GlobalState

    case ActionTypes.SetRoles:
      return state.set('roles', action.roles) as GlobalState

    case ActionTypes.SetSignTypes:
      return state.set('signTypes', action.signTypes) as GlobalState

    case ActionTypes.SetSiteSettings:
      return state.set('siteSettings', action.siteSettings) as GlobalState

    case ActionTypes.SetSocialNetworks:
      return state.set('socialNetworks', action.networks) as GlobalState

    case ActionTypes.SetTeams:
      return state.set('teams', action.teams) as GlobalState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  // For unrecognized actions (or in cases where actions have no effect), must return the existing state
  //  (or default initial state if none was supplied)
  return state
}

// -------------------------
// SAGAS
// -------------------------
const getGlobalState = (state: ApplicationState) =>
  state.global as IGlobalState

function* getCompaniesListSaga() {
  try {
    const url = '/api/Companies/Index'

    const state = yield select(getGlobalState)

    if (
      GlobalState.shouldRequestNewData(
        state.companies.results,
        state.companies.timestamp,
      )
    ) {
      const result = yield makeAuthenticatedRequest(url)

      if (result.success) {
        const companyResult: ICompanyResult = {
          resultCount: result.count,
          results: List(result.results.map((c: ICompany) => Company.fromJS(c))),
          timestamp: Math.round(new Date().getTime() / 1000),
        }
        yield put(setCompaniesList(companyResult))
      }
    }
  } catch (err) {
    console.error(err)
  }
}

function* getCompanyContactsSaga(action: IGetCompanyContactsListAction) {
  try {
    const url = '/api/Contacts/Company/Index'

    const state = yield select(getGlobalState)

    if ((action.forceUpdate ?? false) ||
      GlobalState.shouldRequestNewData(
        state.companyContacts.results,
        state.companyContacts.timestamp,
      )
    ) {
      const result = yield makeAuthenticatedRequest(url)

      if (result.success) {
        const companyContactsResult = new CompanyContactsResult({
          resultCount: result.count,
          results: List(
            result.results.map((r: IAssociatedContact) =>
              AssociatedContact.fromJS(r),
            ),
          ),
          timestamp: Math.round(new Date().getTime() / 1000),
        })
        yield put(setCompanyContactsList(companyContactsResult))
      }
    }
  } catch (err) {
    console.error(err)
  }
}

function* getCompsSaga(action: IGetCompsAction) {
  try {
    if (!action.filter || (action.filter && action.filter.length < 3)) {
      yield put(setComps([]))
      return
    }

    const baseUrl = `/api/Properties/Comps${
      action.filter ? `?filter=${action.filter}` : ''
    }`

    const result = yield makeAuthenticatedRequest(baseUrl)

    if (result.success === true) {
      const list: Comp[] = result.results.map((c: any) => Comp.fromJS(c))
      yield put(setComps(list))
    }
  } catch (err) {
    console.error(err)
  }
}

function* getConfigurationSaga() {
  try {
    const result = yield makeAuthenticatedRequest('/api/Configuration')

    if (result.success === true) {
      yield put(setConfiguration(Configuration.fromJS(result)))
    } else {
      console.error(result.message)
    }
  } catch (err) {
    console.error(err)
  }
}

function* getContactEmailAddressesSaga() {
  try {
    const state: IGlobalState = yield select(getGlobalState)

    if (
      GlobalState.shouldRequestNewData(
        state.contactEmailAddresses.contacts,
        state.contactEmailAddresses.timestamp,
      )
    ) {
      const result = yield makeAuthenticatedRequest(
        '/api/Contacts/EmailAddresses',
      )
      if (result.success === true) {
        const contactEmailAddresses = new ContactEmailAddressesResult({
          contacts: List(
            result.contacts.map((r: IShareContact) => ShareContact.fromJS(r)),
          ),
          timestamp: Math.round(new Date().getTime() / 1000),
        })
        yield put(setContactEmailAddresses(contactEmailAddresses))
      } else {
        console.error(result.message)
      }
    }
  } catch (err) {
    console.error(err)
  }
}

function* getDevelopmentSiteMetadataSaga() {
  try {
    const result = yield makeAuthenticatedRequest(
      '/api/developmentSites/my/meta',
    )

    if (result.success === true) {
      yield put(
        setDevelopmentSiteMetadata(
          DevelopmentSiteMetadataResponse.fromJS(result),
        ),
      )
    } else {
      console.error(result.message)
    }
  } catch (err) {
    console.error(err)
  }
}

function* getGroupsSaga() {
  try {
    const result = yield makeAuthenticatedRequest('/api/Groups/Index')

    if (result.success === true) {
      const list: Group[] = result.results.map((a: any, index: any) => {
        a.id = index
        return Group.fromJS(a)
      })

      yield put(setGroups(list))
    } else {
      console.error(result.message)
    }
  } catch (err) {
    console.error(err)
  }
}

function* getHbContactsSaga() {
  try {
    const result = yield makeAuthenticatedRequest('/api/Users/Index')

    if (result.success === true) {
      const contactList = List(
        result.results.map((a: IAssociatedContact) =>
          AssociatedContact.fromJS(a),
        ),
      ) as List<AssociatedContact>

      yield put(setHbContacts(contactList))
    } else {
      console.error(result.message)
    }
  } catch (err) {
    console.error(err)
  }
}

function* getPlacesSaga(action: IGetPlacesAction) {
  try {
    if (!action.filter || (action.filter && action.filter.length < 4)) {
      yield put(setPlaces([]))
      return
    }

    const baseUrl = `/api/NationalPlaces${
      action.filter ? `?q=${action.filter}` : ''
    }`

    const result = yield makeAuthenticatedRequest(baseUrl)

    if (result.success === true) {
      const places: NationalPlace[] = result.results.map((c: any) =>
        NationalPlace.fromJS(c),
      )
      yield put(setPlaces(places))
    }
  } catch (err) {
    console.error(err)
  }
}

function* getPropertiesListSaga() {
  try {
    const url = '/api/Properties/Index'

    const state = yield select(getGlobalState)

    if (
      GlobalState.shouldRequestNewData(
        state.properties.results,
        state.properties.timestamp,
      )
    ) {
      const result = yield makeAuthenticatedRequest(url)

      if (result.success) {
        const propertiesResult = new PropertyResult({
          resultCount: result.count,
          results: List(
            result.results.map((r: IAssociatedProperty) =>
              AssociatedProperty.fromJS(r),
            ),
          ),
          timestamp: Math.round(new Date().getTime() / 1000),
        })
        yield put(setPropertiesList(propertiesResult))
      }
    }
  } catch (err) {
    console.error(err)
  }
}

function* getRolesSaga() {
  try {
    const result = yield makeAuthenticatedRequest('/api/Roles')

    if (result.success === true) {
      const list = List(
        result.results.map((a: IRole) => Role.fromJS(a)),
      ) as List<Role>

      yield put(setRolesList(list))
    } else {
      console.error(result.message)
    }
  } catch (err) {
    console.error(err)
  }
}

function* getSignTypesSaga() {
  try {
    const result = yield makeAuthenticatedRequest('/api/signTypes')

    if (result.success === true) {
      const list = List(
        result.results.map((a: ISignType) => SignType.fromJS(a)),
      ) as List<SignType>

      yield put(setSignTypes(list))
    } else {
      console.error(result.message)
    }
  } catch (err) {
    console.error(err)
  }
}

function* getSiteSettingsSaga() {
  try {
    const result = yield makeAuthenticatedRequest(
      '/api/Configuration/Settings',
    )

    if (result.success === true) {
      yield put(setSiteSettings(SiteSettings.fromJS(result.entity)))
    } else {
      console.error(result.message)
    }
  } catch (err) {
    console.error(err.message)
  }
}

function* getSocialNetworksSaga() {
  try {
    const url = '/api/SocialNetworks'

    const state = yield select(getGlobalState)

    const result = yield makeAuthenticatedRequest(url)

    if (result.success) {
      yield put(
        setSocialNetworksList(
          List(result.results.map((a: any) => SocialNetwork.fromJS(a))),
        ),
      )
    }
  } catch (err) {
    console.error(err)
  }
}

function* getTeamsSaga() {
  try {
    const result = yield makeAuthenticatedRequest('/api/Teams/All')

    if (result.success === true) {
      const list = List(
        result.results.map((a: ITeam) => Team.fromJS(a)),
      ) as List<Team>

      yield put(setTeams(list))
    } else {
      console.error(result.message)
    }
  } catch (err) {
    console.error(err)
  }
}

function* initializeGlobalSaga(action: IInitializeGlobalAction) {
  try {
    yield put(resetState())

    yield put(getConfiguration())
    yield put(getSocialNetworksList())
    yield put(getSiteSettings())
    yield put(getCompaniesList())
    yield put(getCompanyContactsList())
    yield put(getGroups())
    yield put(getHbcontacts())
    yield put(getPropertiesList())
    yield put(getRolesList())
    yield put(getSignTypes())
    yield put(getTeams())

    if (
      action.user.permissions.isDevelopmentUser ||
      action.user.permissions.isAdmin ||
      action.user.permissions.isGuest
    ) {
      yield put(getDevelopmentSiteMetadata())
    }
  } catch (err) {
    console.error(err)
  }
}

function* watchGetCompaniesList() {
  yield takeLatest(ActionTypes.GetCompaniesList, getCompaniesListSaga)
}

function* watchGetCompanyContactsList() {
  yield takeLatest(ActionTypes.GetCompanyContactsList, getCompanyContactsSaga)
}

function* watchGetComps() {
  yield takeLatest(ActionTypes.GetComps, getCompsSaga)
}

function* watchGetConfiguration() {
  yield takeLatest(ActionTypes.GetConfiguration, getConfigurationSaga)
}

function* watchGetContactEmailAddresses() {
  yield takeLatest(
    ActionTypes.GetContactEmailAddresses,
    getContactEmailAddressesSaga,
  )
}

function* watchGetDevelopmentSiteMetadata() {
  yield takeLatest(
    ActionTypes.GetDevelopmentSiteMetadata,
    getDevelopmentSiteMetadataSaga,
  )
}

function* watchGetGroups() {
  yield takeLatest(ActionTypes.GetGroupList, getGroupsSaga)
}

function* watchGetHbContacts() {
  yield takeLatest(ActionTypes.GetHbContacts, getHbContactsSaga)
}

function* watchGetPlaces() {
  yield takeLatest(ActionTypes.GetPlaces, getPlacesSaga)
}

function* watchGetPropertiesList() {
  yield takeLatest(ActionTypes.GetPropertiesList, getPropertiesListSaga)
}

function* watchGetRoles() {
  yield takeLatest(ActionTypes.GetRoles, getRolesSaga)
}

function* watchGetSignTypes() {
  yield takeLatest(ActionTypes.GetSignTypes, getSignTypesSaga)
}

function* watchGetSiteSettings() {
  yield takeLatest(ActionTypes.GetSiteSettings, getSiteSettingsSaga)
}

function* watchGetSocialNetworksList() {
  yield takeLatest(ActionTypes.GetSocialNetworks, getSocialNetworksSaga)
}

function* watchGetTeams() {
  yield takeLatest(ActionTypes.GetTeams, getTeamsSaga)
}

function* watchInitializeGlobal() {
  yield takeLatest(ActionTypes.InitializeGlobal, initializeGlobalSaga)
}

export function* globalRootSaga() {
  yield fork(watchGetCompaniesList)
  yield fork(watchGetCompanyContactsList)
  yield fork(watchGetComps)
  yield fork(watchGetConfiguration)
  yield fork(watchGetContactEmailAddresses)
  yield fork(watchGetDevelopmentSiteMetadata)
  yield fork(watchGetGroups)
  yield fork(watchGetHbContacts)
  yield fork(watchGetPlaces)
  yield fork(watchGetPropertiesList)
  yield fork(watchGetRoles)
  yield fork(watchGetSignTypes)
  yield fork(watchGetSiteSettings)
  yield fork(watchGetSocialNetworksList)
  yield fork(watchGetTeams)
  yield fork(watchInitializeGlobal)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = (state: ApplicationState): GlobalState =>
  state.global

export const selectCompanyList = () =>
  createSelector(
    selectModuleState,
    (global) => global.companies,
  )

export const selectCompanyContacts = () =>
  createSelector(
    selectModuleState,
    (global) => global.companyContacts,
  )

export const selectComps = () =>
  createSelector(
    selectModuleState,
    (global) => global.comps,
  )

export const selectConfiguration = () =>
  createSelector(
    selectModuleState,
    (global) => global.configuration,
  )

export const selectContactEmailAddresses = () =>
  createSelector(
    selectModuleState,
    (global) => global.contactEmailAddresses,
  )

export const selectDevelopmentSiteMetadata = () =>
  createSelector(
    selectModuleState,
    (global) => global.developmentSiteMetadata,
  )

export const selectGroups = () =>
  createSelector(
    selectModuleState,
    (global) => global.groups.toArray(),
  )

export const selectHbContacts = () =>
  createSelector(
    selectModuleState,
    (global) => global.hbContacts,
  )

export const selectPlaces = () =>
  createSelector(
    selectModuleState,
    (global) => global.places,
  )

export const selectPropertyList = () =>
  createSelector(
    selectModuleState,
    (global) => global.properties,
  )

export const selectRolesList = () =>
  createSelector(
    selectModuleState,
    (global) => global.roles,
  )

export const selectSignTypes = () =>
  createSelector(
    selectModuleState,
    (global) => global.signTypes,
  )

export const selectSiteSettings = () =>
  createSelector(
    selectModuleState,
    (mState) => mState.siteSettings,
  )

export const selectSocialNetwork = (socialNetworkId: string) =>
  createSelector(
    selectSocialNetworks(),
    (socialNetworks) =>
      socialNetworks.find((s) => s !== undefined && s.id === socialNetworkId),
  )

export const selectSocialNetworks = () =>
  createSelector(
    selectModuleState,
    (global) => global.socialNetworks,
  )

export const selectTeams = () =>
  createSelector(
    selectModuleState,
    (global) => global.teams,
  )

export const selectGlobalState = () =>
  createSelector(
    selectModuleState,
    (global) =>
      ({
        companies: global.companies,
        companyContacts: global.companyContacts,
        comps: global.comps,
        configuration: global.configuration,
        contactEmailAddresses: global.contactEmailAddresses,
        developmentSiteMetadata: global.developmentSiteMetadata,
        groups: global.groups,
        hbContacts: global.hbContacts,
        places: global.places,
        properties: global.properties,
        roles: global.roles,
        signTypes: global.signTypes,
        siteSettings: global.siteSettings,
        socialNetworks: global.socialNetworks,
        teams: global.teams,
      } as IGlobalState),
  )
